<template>
    <div id="ChatBotAiFaq">
        <CreateFaq v-if="showCreateFaq" :storyId="storyId" @close="showCreateFaq = false" @save-faq="saveFaq" />
        <template v-else>
            <PreviewFaq
                :knowledge-base="showingContent"
                v-if="showingContent"
                @close="showingContent = null"
                :story-id="storyId"
                @deleted="() => onDeletedSingle(showingContent._id)"
                @changed="(enabled) => onChangedStateSingle(showingContent._id, enabled)"
                @update-faq="updateFaq"
            />
            <div class="faq-container" v-else-if="!showingContent">
                <div class="header">
                    <div class="title">{{ $t("chatbotv2.frequentlyAskedQuestions") }}</div>
                    <div class="actions">
                        <SupsisButton primary @click="showCreateFaq = true">
                            <i class="material-icons">add</i>
                            <span>{{ $t("chatbotv2.addContent") }}</span>
                        </SupsisButton>
                    </div>
                </div>
                <div>
                    <ve-pagination
                        :total="total"
                        :page-index="page"
                        :page-size="pageSize"
                        :page-size-option="[10, 15, 20, 50, 100]"
                        @on-page-number-change="(val) => (page = val)"
                        @on-page-size-change="(val) => (pageSize = val)"
                    />
                </div>
                <div class="content">
                    <div class="faq-table">
                        <div class="faq-table-header">
                            <div
                                class="faq-table-header-item"
                                v-for="column in columns"
                                :key="column.id"
                                :style="{
                                    flex: column.flex,
                                    width: column.width ? column.width + 'px' : 'auto',
                                    'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto',
                                }"
                            >
                                <a-checkbox
                                    v-if="column.isCheckbox"
                                    @click="toggleSelectAll"
                                    :checked="isSelectAll()"
                                />
                                <template v-else>{{ column.title }}</template>
                            </div>
                        </div>
                        <div class="faq-table-body">
                            <div class="row" v-for="row in filteredRows" :key="row._id">
                                <div
                                    class="cell"
                                    v-for="column in columns"
                                    :key="column.id"
                                    :style="{
                                        flex: column.flex,
                                        width: column.width ? column.width + 'px' : 'auto',
                                        'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto',
                                    }"
                                    @click="previewArticles(row)"
                                >
                                    <template v-if="column.slot === 'state'">
                                        <div class="d-flex w-100">
                                            <div v-if="row.enabled" class="state-cell active">
                                                {{ $t("chatbotv2.usedByAi") }}
                                            </div>
                                            <div v-else class="state-cell passive">{{ $t("chatbotv2.notUsed") }}</div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <template v-if="column.isCheckbox">
                                            <a-checkbox :checked="isSelected(row)" @click.stop="() => toggleSelect(row)" />
                                        </template>
                                        <template v-else>
                                            <span v-if="column.getData">{{ column.getData(row) }}</span>
                                            <span v-else>{{ row[column.id] }}</span>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="select-panel-wrapper" v-if="selectedItems.length">
                        <div class="select-panel">
                            <div class="items-count">
                                <i class="material-icons spin" v-if="busy">"autorenew"</i>
                                <i class="material-icons" v-else>check</i>
                                <div>{{ selectedItems.length }} {{ $t("message.selected") }}</div>
                            </div>
                            <div class="buttons">
                                <SupsisButton small :disabled="busy" @click="() => deleteMulti()"
                                    ><i class="material-icons">delete</i>
                                </SupsisButton>
                                <SupsisButton small :disabled="busy"
                                    ><i class="material-icons">refresh</i></SupsisButton
                                >
                                <SupsisButton :disabled="busy" @click="() => multiChangeState(false)">
                                    <i class="material-icons">toggle_off</i>
                                    {{ $t("chatbotv2.disableAi") }}
                                </SupsisButton>
                                <SupsisButton :disabled="busy" @click="() => multiChangeState(true)">
                                    <i class="material-icons">toggle_on</i>
                                    {{ $t("chatbotv2.enableAi") }}
                                </SupsisButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import ComingSoon from "../ComingSoon.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import moment from "moment";
import ContentPreview from "../ContentPreview.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import CreateFaq from "./CreateFaq.vue";
import PreviewFaq from "./PreviewFaq.vue";

export default {
    name: "FaqList",
    components: {
        ContentPreview,
        SupsisButton,
        ComingSoon,
        CreateFaq,
        PreviewFaq,
    },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            total: 0,
            page: 1,
            pageSize: 10,
            showingContent: null,
            showCreateFaq: false,
            selectedItems: [],
            columns: [
                {
                    title: "",
                    id: "checkbox",
                    isCheckbox: true,
                    width: 50,
                    maxWidth: 50,
                },
                {
                    title: this.$t("message.title"),
                    id: "faq.question",
                    flex: 2,
                    getData: (row) => {
                        return row.faq.question;
                    },
                },
                {
                    title: this.$t("message.createdAt"),
                    id: "createdAt",
                    getData: (row) => {
                        return moment(row.createdAt).format("DD/MM/YYYY");
                    },
                },
                {
                    title: this.$t("chatbotv2.state"),
                    id: "state",
                    slot: "state",
                },
            ],
            rows: [],
            loading: true,
            busy: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        filteredRows() {
            return this.rows.filter((row) => row.faq && row.faq.question);
        },
    },
    methods: {
        ...mapActions({}),
        openAiAdded() {
            sendSocketMessage("chatbot/v2 kb-faq-added", {
                siteId: this.site._id,
                storyId: this.storyId,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        openAiRemoved() {
            sendSocketMessage("chatbot/v2 kb-faq-removed", {
                siteId: this.site._id,
                storyId: this.storyId,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        selectAll() {
            this.selectedItems = this.rows;
        },
        deselectAll() {
            this.selectedItems = [];
        },
        isSelectAll() {
            return this.selectedItems.length === this.rows.length;
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.rows.length) {
                this.deselectAll();
            } else {
                this.selectAll();
            }
        },
        toggleSelect(item) {
            const isSelected = this.selectedItems.find((i) => i._id === item._id);
            if (isSelected) {
                this.selectedItems = this.selectedItems.filter((i) => i._id !== item._id);
            } else {
                this.selectedItems.push(item);
            }
        },
        isSelected(item) {
            return !!this.selectedItems.find((i) => i._id === item._id);
        },
        reTrain() {
            sendSocketMessage("chatbot/v2 complete-train", {
                siteId: this.siteId,
                storyId: this.storyId,
                pageIds: [],
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        fetch(page, pageSize) {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "faq", page, pageSize).then((res) => {
                this.rows = res.data.rows;
                this.total = res.data.total;
                this.loading = false;
            });
        },
        onDeletedSingle(id) {
            this.openAiRemoved();
            this.$message.success("FAQ is deleted.");
            this.rows = this.rows.filter((i) => i._id !== id);
            this.showingContent = null;
            this.selectedItems = [];
            this.reTrain();
            this.$emit("trained");
            this.openAiAdded();
        },
        onChangedStateSingle(id, enabled) {
            this.$message.success(
                enabled ? "Selected knowledge bases are enabled." : "Selected knowledge bases are disabled."
            );
            this.rows = this.rows.map((x) => {
                if (x._id === id) {
                    x.enabled = enabled;
                }
                return x;
            });
            this.selectedItems = [];
            this.reTrain();
            this.$emit("trained");
            this.showingContent = null;
        },
        deleteMulti() {
            const selectedIds = this.selectedItems.map((i) => i._id);
            this.busy = true;
            const hide = this.$message.loading("Selected knowledge bases are being deleted...", 0);
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, selectedIds)
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$message.success("Selected knowledge bases are deleted.");
                    this.selectedItems = [];
                    this.reTrain();
                    this.rows = this.rows.filter((i) => !selectedIds.includes(i._id));
                    this.$emit("trained");
                    this.openAiRemoved();
                    this.openAiAdded();
                    this.$emit("updated");
                })
                .catch((error) => {
                    this.$message.error("Selected knowledge bases are not deleted. Please try again.");
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        multiChangeState(enabled) {
            const selectedIds = this.selectedItems.map((i) => i._id);
            this.busy = true;
            const hide = this.$message.loading(
                enabled ? this.$t("chatbotv2.selectKnowEnabled") : this.$t("chatbotv2.selectKnowDisabled"),
                0
            );
            CHATBOT_API.KNOWLEDGE_BASE.changeStateMultiKnowledgeBase(this.storyId, selectedIds, enabled)
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$message.success(
                        enabled ? "Selected knowledge bases are enabled." : "Selected knowledge bases are disabled."
                    );
                    this.selectedItems = [];
                    this.reTrain();
                    this.fetch();
                    this.$emit("trained");
                    this.openAiRemoved();
                    this.openAiAdded();
                    this.$emit("updated");
                })
                .catch((error) => {
                    this.$message.error(
                        enabled ? this.$t("chatbotv2.selectKnowNotEnabled") : this.$t("chatbotv2.selectKnowNotDisabled")
                    );
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        async saveFaq(payload) {
            const kb = await CHATBOT_API.KNOWLEDGE_BASE.createKnowledgeBase(this.storyId, "faq", undefined, undefined, {
                question: payload.question,
                answer: payload.answer,
            }).then((res) => res.data);
            this.rows.push(kb);
            if (this.rows.length > 0) {
                this.openAiRemoved();
            }
            this.openAiAdded();
            this.$message.success(this.$t("chatbotv2.faqIsCreated"));
            this.showCreateFaq = false;
            this.$emit("updated");
        },
        async updateFaq(id, payload) {
            await CHATBOT_API.KNOWLEDGE_BASE.knowledgeBaseUpdate(this.storyId, id, payload)
                .then((res) => res.data)
                .then((data) => {
                    this.rows = this.rows.map((row) => {
                        if (row._id === id) row = data;
                        return row;
                    });
                    this.showingContent = null;
                });
            this.openAiRemoved();
            this.openAiAdded();
        },
        previewArticles(row) {
            this.showingContent = row;
            this.showCreateFaq = false;
        },
    },
    mounted() {
        this.fetch();
    },
    watch: {
        page() {
            this.fetch(this.page, this.pageSize);
        },
        pageSize() {
            this.fetch(this.page, this.pageSize);
        },
    },
};
</script>
<style lang="scss" scoped>
#ChatBotAiFaq {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    width: 100%;
    height: 100%;

    .faq-container {
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 0px;
            width: 100%;

            .title {
                color: #22272d;
                display: flex;
                font-size: 22px;
                font-weight: 600;
                line-height: 28px;
                align-items: center;
            }
        }

        .content {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 20px;
        }

        .faq-table {
            width: 100%;

            .faq-table-header {
                display: flex;
                align-items: center;
                width: 100%;
                padding: 10px;

                .faq-table-header-item {
                    flex: 1;
                }
            }

            .faq-table-body {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                align-items: center;
                overflow-y: auto;
                height: 100%;
                max-height: calc(100vh - 100px);

                .row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding: 10px;
                    border-radius: 5px;
                    background: var(--color-bg-neutral-default);
                    cursor: pointer;

                    &:hover {
                        opacity: 0.8;
                    }

                    .cell {
                        flex: 1;
                        color: #000;
                        font-size: 15px;
                        font-weight: 600;
                        line-height: 19px;
                        overflow: hidden !important;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .state-cell {
                        display: flex;
                        align-items: center;
                        font-size: 14px;
                        font-weight: 600;
                        height: 32px;
                        justify-content: center;
                        line-height: 18px;
                        padding: 0 6px;
                        border-radius: 10px;

                        &.active {
                            background: #fff;
                            color: #00893c;
                            width: min-content;
                        }

                        &.passive {
                            background: #dcdcdc;
                            color: #79797c;
                            width: min-content;
                        }
                    }
                }
            }
        }

        .select-panel-wrapper {
            bottom: 10px;
            left: 36px;
            position: absolute;
            right: 36px;

            .select-panel {
                background: #22272d;
                border-radius: 15px;
                box-shadow: 0 -4px 32px 0 rgba(0, 0, 0, 0.16);
                color: #fff;
                height: 64px;
                align-items: center;
                display: flex;
                justify-content: center;

                .items-count {
                    align-items: center;
                    display: flex;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 20px;
                    margin-left: 16px;
                }

                .buttons {
                    align-items: center;
                    display: flex;
                    gap: 8px;
                    justify-content: center;
                    margin-left: auto;
                    margin-right: 16px;
                }
            }
        }
    }
}
</style>
